import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Calculator, Check, Loader2, Ruler, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";

import { useBidSheetCalculations } from "./useBidSheetCalculations";
import { BidSummaryCard } from "./BidSummaryCard";
import { MaterialsSection } from "./MaterialsSection";
import { EquipmentSection } from "./EquipmentSection";
import { MiscSection } from "./MiscSection";
import { LaborSection } from "./LaborSection";
import { fuzzyMatch } from "../../lib/searchUtils";
import { formatCurrency as formatCurrencyBase } from "../../lib/formatters";

// Module-level currency formatters (stable reference — no useCallback needed)
const formatCurrency = (amount) => formatCurrencyBase(amount, true);
const formatWholeDollars = (amount) => formatCurrencyBase(amount);

/**
 * BidSheetEditor - Main component for editing bid sheets
 * Reusable for base bids and change orders
 * Handles auto-save, calculations, and labor entry management
 */
export const BidSheetEditor = ({
  initialBidSheet,
  materials,
  equipment,
  employeeRoles = [],
  miscItemsFromSettings = [],
  miscUnits = [],
  defaultBurdenPercent = 28,
  defaultSalesTaxPercent = 8.25,
  canManageJobs,
  onSaveBidSheet,
  title = "Bid Sheet",
  takeoffData,
  changeOrders = [],  // Pass change orders for summary display
  effectiveTotal = null,  // job.estimate_total from backend
}) => {
  // Initialize bid sheet with defaults
  const getDefaultBidSheet = useCallback(() => ({
    id: crypto.randomUUID(),
    name: "Base Bid",
    material_items: [],
    equipment_items: [],
    labor_entries: [],
    misc_items: [],
    burden_percent: defaultBurdenPercent,
    sales_tax_percent: defaultSalesTaxPercent,
    profit_percent: 25,
    tax_exempt: false,
    scaffold_sqft: 0,
    estimated_weeks: 0,
  }), [defaultBurdenPercent, defaultSalesTaxPercent]);

  const [bidSheet, setBidSheet] = useState(() => initialBidSheet || getDefaultBidSheet());
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved"); // "saved", "saving", "unsaved"
  const [bidItemSearch, setBidItemSearch] = useState("");
  const [preSelectMaterial, setPreSelectMaterial] = useState(null);
  const [preSelectEquipment, setPreSelectEquipment] = useState(null);
  const [preSelectMisc, setPreSelectMisc] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Use the calculations hook
  const calculations = useBidSheetCalculations(bidSheet);

  // Takeoff summary - compute totals per material from takeoff data
  const takeoffSummary = useMemo(() => {
    if (!takeoffData?.columns) return [];
    return takeoffData.columns
      .filter(col => col.material_id && col.material_name)
      .map(col => {
        const isLinear = col.measure_mode === "linear";
        const rows = col.rows || [];
        const totalSF = isLinear
          ? rows.reduce((sum, r) => sum + (parseFloat(r.length) || 0), 0)
          : rows.reduce((sum, r) => sum + (parseFloat(r.length) || 0) * (parseFloat(r.width) || 0), 0);
        const factor = parseFloat(col.conversion_factor) || 0;
        let totalQty;
        switch (col.conversion_method) {
          case "units_per_sf":
          case "units_per_lf":
            totalQty = totalSF * factor; break;
          case "by_sf":
          case "by_lf":
            totalQty = totalSF; break;
          case "per_piece":
            totalQty = rows.filter(r => (parseFloat(r.length) || 0) > 0).length; break;
          default:
            totalQty = totalSF;
        }
        const mat = materials.find(m => m.id === col.material_id);
        return {
          id: col.id,
          material_id: col.material_id,
          material_name: col.material_name,
          category: mat?.category || "",
          notes: col.notes || "",
          unit: mat?.unit || (isLinear ? "LF" : "SF"),
          measure_label: isLinear ? "LF" : "SF",
          total_measure: Math.round(totalSF * 100) / 100,
          total_qty: Math.ceil(totalQty),
          conversion_method: col.conversion_method,
        };
      })
      .filter(s => s.total_measure > 0);
  }, [takeoffData, materials]);

  // Track checked-off takeoff items (persisted in bid sheet)
  const [checkedTakeoffItems, setCheckedTakeoffItems] = useState(() =>
    (initialBidSheet?.takeoff_checked || [])
  );

  const toggleTakeoffCheck = useCallback((colId) => {
    setCheckedTakeoffItems(prev => {
      const next = prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId];
      // Save to bid sheet
      setBidSheet(p => ({ ...p, takeoff_checked: next }));
      setHasChanges(true);
      setSaveStatus("unsaved");
      return next;
    });
  }, []);

  // Universal bid item search - searches materials, equipment, misc
  const bidItemSearchResults = useMemo(() => {
    if (!bidItemSearch.trim()) return [];
    const results = [];

    materials.forEach((m) => {
      if (fuzzyMatch(bidItemSearch, (m.name || "") + " " + (m.category || ""))) {
        results.push({ id: m.id, name: m.name, type: "material", category: m.category, subtitle: `${m.category} · $${m.unit_price}/${m.unit}` });
      }
    });

    equipment.forEach((e) => {
      if (fuzzyMatch(bidItemSearch, (e.name || "") + " " + (e.category || ""))) {
        results.push({ id: e.id, name: e.name, type: "equipment", category: e.category, subtitle: `${e.category}${e.unit_price ? ` · $${e.unit_price}/${e.unit}` : ""}` });
      }
    });

    miscItemsFromSettings.forEach((m) => {
      if (fuzzyMatch(bidItemSearch, (m.description || "") + " " + (m.category || ""))) {
        results.push({ id: m.id, name: m.description, type: "misc", category: m.category || "Misc", subtitle: `${m.category || "Misc"}${m.unit_price ? ` · $${m.unit_price}/${m.unit || "each"}` : ""}` });
      }
    });

    return results.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [bidItemSearch, materials, equipment, miscItemsFromSettings]);

  const handleBidItemSelect = useCallback((item) => {
    if (item.type === "material") setPreSelectMaterial({ id: item.id });
    else if (item.type === "equipment") setPreSelectEquipment({ id: item.id });
    else if (item.type === "misc") setPreSelectMisc({ id: item.id });
    setBidItemSearch("");
  }, []);

  const clearPreSelect = useCallback(() => {
    setPreSelectMaterial(null);
    setPreSelectEquipment(null);
    setPreSelectMisc(null);
  }, []);

  // Currency formatters are now imported from lib/formatters

  // Handle settings changes
  const handleSettingsChange = useCallback((field, value) => {
    setBidSheet(prev => {
      const updated = { ...prev, [field]: value };
      
      // When estimated_weeks changes, recalculate all manual labor entries
      if (field === "estimated_weeks") {
        const newHours = (parseFloat(value) || 0) * 5 * 8;
        updated.labor_entries = (prev.labor_entries || []).map(entry => {
          // Skip auto-generated entries (from materials, equipment, misc)
          if (entry.is_material_labor || entry.is_equipment_labor || entry.is_misc_labor) {
            return entry;
          }
          const amount = (entry.hourly_rate || 0) * newHours;
          return {
            ...entry,
            hours: newHours,
            amount,
            description: `${entry.role} - ${newHours} hours @ ${formatCurrency(entry.hourly_rate)}/hr`,
          };
        });
      }
      
      return updated;
    });
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, [formatCurrency]);

  // Save handler
  const handleSave = useCallback(async () => {
    setSaveStatus("saving");
    const updatedBidSheet = {
      ...bidSheet,
      material_subtotal: calculations.materialSubtotal,
      labor_subtotal: calculations.laborSubtotal,
      equipment_subtotal: calculations.equipmentSubtotal,
      burden_amount: calculations.burdenAmount,
      tax_amount: calculations.taxAmount,
      subtotal_before_profit: calculations.subtotalBeforeProfit,
      profit_amount: calculations.profitAmount,
      grand_total: calculations.grandTotal,
      price_per_sqft: calculations.pricePerSqft,
    };

    const success = await onSaveBidSheet([updatedBidSheet]);
    if (success) {
      setHasChanges(false);
      setSaveStatus("saved");
    } else {
      setSaveStatus("unsaved");
    }
  }, [bidSheet, calculations, onSaveBidSheet]);

  // Auto-save effect
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!hasChanges) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 1500);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasChanges, handleSave]);

  // Material handlers
  const handleAddMaterial = useCallback((newItem) => {
    setBidSheet(prev => {
      let updatedLaborEntries = [...prev.labor_entries];
      const laborEntryId = `material-labor-${newItem.id}`;
      
      // Create labor entry if there's any labor cost (positive or negative for deductions)
      if (newItem.labor_total !== 0) {
        const laborEntry = {
          id: laborEntryId,
          role: "Material Labor",
          hourly_rate: 0,
          hours: 0,
          amount: newItem.labor_total,
          notes: `Labor for ${newItem.item_name} (${newItem.quantity} × ${formatCurrency(newItem.labor_per_unit)})`,
          description: `Labor for ${newItem.item_name}`,
          is_material_labor: true,
          material_item_id: newItem.id,
        };
        updatedLaborEntries.push(laborEntry);
      }
      
      return {
        ...prev,
        material_items: [...prev.material_items, newItem],
        labor_entries: updatedLaborEntries,
      };
    });
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, [formatCurrency]);

  const handleEditMaterial = useCallback((updatedItem) => {
    setBidSheet(prev => {
      let updatedLaborEntries = [...prev.labor_entries];
      const laborEntryId = `material-labor-${updatedItem.id}`;
      
      // Remove existing labor entry first
      updatedLaborEntries = updatedLaborEntries.filter(e => e.id !== laborEntryId);
      
      // Add labor entry if there's any labor cost (positive or negative for deductions)
      if (updatedItem.labor_total !== 0) {
        const laborEntry = {
          id: laborEntryId,
          role: "Material Labor",
          hourly_rate: 0,
          hours: 0,
          amount: updatedItem.labor_total,
          notes: `Labor for ${updatedItem.item_name} (${updatedItem.quantity} × ${formatCurrency(updatedItem.labor_per_unit)})`,
          description: `Labor for ${updatedItem.item_name}`,
          is_material_labor: true,
          material_item_id: updatedItem.id,
        };
        updatedLaborEntries.push(laborEntry);
      }
      
      return {
        ...prev,
        material_items: prev.material_items.map(item => 
          item.id === updatedItem.id ? updatedItem : item
        ),
        labor_entries: updatedLaborEntries,
      };
    });
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, [formatCurrency]);

  const handleDeleteMaterial = useCallback((itemId) => {
    setBidSheet(prev => ({
      ...prev,
      material_items: prev.material_items.filter(item => item.id !== itemId),
      labor_entries: prev.labor_entries.filter(e => e.id !== `material-labor-${itemId}`),
    }));
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  // Equipment handlers
  const handleAddEquipment = useCallback((newItem) => {
    setBidSheet(prev => {
      let updatedLaborEntries = [...prev.labor_entries];
      const laborEntryId = `equipment-labor-${newItem.id}`;
      
      if (newItem.labor_cost > 0) {
        const laborEntry = {
          id: laborEntryId,
          role: "Equipment Labor",
          hourly_rate: 0,
          hours: 0,
          amount: newItem.labor_cost,
          notes: `Labor for ${newItem.item_name}`,
          description: `Labor for ${newItem.item_name}`,
          is_equipment_labor: true,
          equipment_item_id: newItem.id,
        };
        updatedLaborEntries.push(laborEntry);
      }
      
      return {
        ...prev,
        equipment_items: [...prev.equipment_items, newItem],
        labor_entries: updatedLaborEntries,
      };
    });
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  const handleEditEquipment = useCallback((updatedItem) => {
    setBidSheet(prev => {
      let updatedLaborEntries = [...prev.labor_entries];
      const laborEntryId = `equipment-labor-${updatedItem.id}`;
      
      updatedLaborEntries = updatedLaborEntries.filter(e => e.id !== laborEntryId);
      
      if (updatedItem.labor_cost > 0) {
        const laborEntry = {
          id: laborEntryId,
          role: "Equipment Labor",
          hourly_rate: 0,
          hours: 0,
          amount: updatedItem.labor_cost,
          notes: `Labor for ${updatedItem.item_name}`,
          description: `Labor for ${updatedItem.item_name}`,
          is_equipment_labor: true,
          equipment_item_id: updatedItem.id,
        };
        updatedLaborEntries.push(laborEntry);
      }
      
      return {
        ...prev,
        equipment_items: prev.equipment_items.map(item => 
          item.id === updatedItem.id ? updatedItem : item
        ),
        labor_entries: updatedLaborEntries,
      };
    });
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  const handleDeleteEquipment = useCallback((itemId) => {
    setBidSheet(prev => ({
      ...prev,
      equipment_items: prev.equipment_items.filter(item => item.id !== itemId),
      labor_entries: prev.labor_entries.filter(e => e.id !== `equipment-labor-${itemId}`),
    }));
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  // Misc handlers
  const handleAddMisc = useCallback((newItem) => {
    setBidSheet(prev => {
      let updatedLaborEntries = [...prev.labor_entries];
      const laborEntryId = `misc-labor-${newItem.id}`;
      
      if (newItem.labor_cost > 0) {
        const laborEntry = {
          id: laborEntryId,
          role: "Misc Labor",
          hourly_rate: 0,
          hours: 0,
          amount: newItem.labor_cost,
          notes: `Labor for ${newItem.description}`,
          description: `Labor for ${newItem.description}`,
          is_misc_labor: true,
          misc_item_id: newItem.id,
        };
        updatedLaborEntries.push(laborEntry);
      }
      
      return {
        ...prev,
        misc_items: [...(prev.misc_items || []), newItem],
        labor_entries: updatedLaborEntries,
      };
    });
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  const handleEditMisc = useCallback((updatedItem) => {
    setBidSheet(prev => {
      let updatedLaborEntries = [...prev.labor_entries];
      const laborEntryId = `misc-labor-${updatedItem.id}`;
      
      updatedLaborEntries = updatedLaborEntries.filter(e => e.id !== laborEntryId);
      
      if (updatedItem.labor_cost > 0) {
        const laborEntry = {
          id: laborEntryId,
          role: "Misc Labor",
          hourly_rate: 0,
          hours: 0,
          amount: updatedItem.labor_cost,
          notes: `Labor for ${updatedItem.description}`,
          description: `Labor for ${updatedItem.description}`,
          is_misc_labor: true,
          misc_item_id: updatedItem.id,
        };
        updatedLaborEntries.push(laborEntry);
      }
      
      return {
        ...prev,
        misc_items: (prev.misc_items || []).map(item => 
          item.id === updatedItem.id ? updatedItem : item
        ),
        labor_entries: updatedLaborEntries,
      };
    });
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  const handleDeleteMisc = useCallback((itemId) => {
    setBidSheet(prev => ({
      ...prev,
      misc_items: (prev.misc_items || []).filter(item => item.id !== itemId),
      labor_entries: prev.labor_entries.filter(e => e.id !== `misc-labor-${itemId}`),
    }));
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  // Labor handlers (direct entries, not from materials/equipment/misc)
  const handleAddLabor = useCallback((newEntry) => {
    setBidSheet(prev => ({
      ...prev,
      labor_entries: [...prev.labor_entries, newEntry],
    }));
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  const handleEditLabor = useCallback((updatedEntry) => {
    setBidSheet(prev => ({
      ...prev,
      labor_entries: prev.labor_entries.map(e => 
        e.id === updatedEntry.id ? updatedEntry : e
      ),
    }));
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  const handleDeleteLabor = useCallback((entryId) => {
    setBidSheet(prev => ({
      ...prev,
      labor_entries: prev.labor_entries.filter(e => e.id !== entryId),
    }));
    setHasChanges(true);
    setSaveStatus("unsaved");
  }, []);

  return (
    <div className="space-y-6">
      {/* Header with Save Status */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{title}: {bidSheet.name}</h3>
          <p className="text-sm text-muted-foreground">Add materials, equipment, and labor to build your estimate</p>
        </div>
        {canManageJobs && (
          <div className="flex items-center gap-2 text-sm" data-testid="save-status">
            {saveStatus === "saving" && (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                <span className="text-blue-600">Saving...</span>
              </>
            )}
            {saveStatus === "saved" && (
              <>
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-green-600">All changes saved</span>
              </>
            )}
            {saveStatus === "unsaved" && (
              <>
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-amber-600">Unsaved changes</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Bid Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Bid Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Profit %</Label>
              <NumericInput
                value={bidSheet.profit_percent}
                onChange={(e) => {}}
                onBlur={(e) => handleSettingsChange("profit_percent", parseFloat(e.target.value) || 0)}
                decimals={2}
                min={0}
                disabled={!canManageJobs}
                data-testid="profit-percent-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Burden % (Labor)</Label>
              <NumericInput
                value={bidSheet.burden_percent}
                onChange={(e) => {}}
                onBlur={(e) => handleSettingsChange("burden_percent", parseFloat(e.target.value) || 0)}
                decimals={2}
                min={0}
                disabled={!canManageJobs}
                data-testid="burden-percent-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Sales Tax % (Materials)</Label>
              <NumericInput
                value={bidSheet.sales_tax_percent}
                onChange={(e) => {}}
                onBlur={(e) => handleSettingsChange("sales_tax_percent", parseFloat(e.target.value) || 0)}
                decimals={2}
                min={0}
                disabled={!canManageJobs || bidSheet.tax_exempt}
                data-testid="sales-tax-percent-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Scaffold Sq. Ft.</Label>
              <NumericInput
                value={bidSheet.scaffold_sqft}
                onChange={(e) => {}}
                onBlur={(e) => handleSettingsChange("scaffold_sqft", parseFloat(e.target.value) || 0)}
                decimals={2}
                min={0}
                disabled={!canManageJobs}
                placeholder="0"
                data-testid="scaffold-sqft-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Est. Weeks</Label>
              <NumericInput
                value={bidSheet.estimated_weeks}
                onChange={(e) => {}}
                onBlur={(e) => handleSettingsChange("estimated_weeks", parseFloat(e.target.value) || 0)}
                decimals={2}
                min={0}
                disabled={!canManageJobs}
                placeholder="0"
                data-testid="estimated-weeks-input"
              />
              <p className="text-xs text-muted-foreground">5 days × 8 hrs/day</p>
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-2">
            <Checkbox
              id="tax-exempt"
              checked={bidSheet.tax_exempt}
              onCheckedChange={(checked) => handleSettingsChange("tax_exempt", checked)}
              disabled={!canManageJobs}
              data-testid="tax-exempt-checkbox"
            />
            <Label htmlFor="tax-exempt" className="cursor-pointer">
              Tax Exempt (Government Job)
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Take Off Summary */}
      {takeoffSummary.length > 0 && (
        <Card data-testid="takeoff-summary-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Ruler className="w-4 h-4" />
              Take Off Quantities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {takeoffSummary.map(item => {
                const isChecked = checkedTakeoffItems.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                      isChecked ? "bg-muted/60 border-muted" : "bg-background hover:bg-muted/30"
                    }`}
                    onClick={() => toggleTakeoffCheck(item.id)}
                    data-testid={`takeoff-check-${item.id}`}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleTakeoffCheck(item.id)}
                      className="pointer-events-none"
                    />
                    <div className={`flex-1 min-w-0 ${isChecked ? "opacity-50 line-through" : ""}`}>
                      <div className="text-sm font-medium truncate">{item.material_name}</div>
                      {(item.category || item.notes) && (
                        <div className="text-xs text-muted-foreground truncate">
                          {item.category}{item.category && item.notes ? " · " : ""}{item.notes}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {item.total_measure.toLocaleString()} {item.measure_label}
                        {item.conversion_method !== "by_sf" && item.conversion_method !== "by_lf" && (
                          <span className="ml-1">
                            = <span className="font-semibold text-foreground">{item.total_qty.toLocaleString()}</span> {item.unit}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-muted-foreground text-right">
              {checkedTakeoffItems.length} of {takeoffSummary.length} added to bid
            </div>
          </CardContent>
        </Card>
      )}

      {/* Universal Bid Item Search */}
      {canManageJobs && (
        <div className="relative" data-testid="bid-item-search-container">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary z-10" />
          <Input
            placeholder="Search all bid items (materials, equipment, misc)..."
            value={bidItemSearch}
            onChange={(e) => setBidItemSearch(e.target.value)}
            className="pl-12 h-12 text-base bg-white border-2 border-primary/40 shadow-md focus-visible:border-primary focus-visible:ring-primary/30 placeholder:text-slate-500"
            data-testid="bid-item-search-input"
          />
          {bidItemSearch.trim() && (
            <Card className="absolute z-50 w-full mt-1 shadow-lg">
              <div className="max-h-[40vh] overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
                {bidItemSearchResults.length > 0 ? (
                  bidItemSearchResults.map((item) => (
                    <button
                      key={`${item.type}-${item.id}`}
                      className="flex items-center justify-between w-full px-4 py-3 text-sm hover:bg-accent cursor-pointer text-left border-b last:border-0"
                      onClick={() => handleBidItemSelect(item)}
                      data-testid={`bid-search-result-${item.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{item.name}</p>
                        {item.subtitle && (
                          <p className="text-xs text-muted-foreground">{item.subtitle}</p>
                        )}
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ml-3 whitespace-nowrap ${
                        item.type === "material" ? "bg-blue-100 text-blue-700"
                        : item.type === "equipment" ? "bg-green-100 text-green-700"
                        : "bg-purple-100 text-purple-700"
                      }`}>
                        {item.type === "material" ? "Material" : item.type === "equipment" ? "Equipment" : "Misc"}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-4 text-sm text-muted-foreground text-center">
                    No items matching "{bidItemSearch}"
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Materials Section */}
      <MaterialsSection
        materialItems={bidSheet.material_items}
        materials={materials}
        canManageJobs={canManageJobs}
        formatCurrency={formatCurrency}
        onAddItem={handleAddMaterial}
        onEditItem={handleEditMaterial}
        onDeleteItem={handleDeleteMaterial}
        materialSubtotal={calculations.materialSubtotal}
        preSelectItem={preSelectMaterial}
        onPreSelectHandled={clearPreSelect}
      />

      {/* Equipment Section */}
      <EquipmentSection
        equipmentItems={bidSheet.equipment_items}
        equipment={equipment}
        canManageJobs={canManageJobs}
        formatCurrency={formatCurrency}
        onAddItem={handleAddEquipment}
        onEditItem={handleEditEquipment}
        onDeleteItem={handleDeleteEquipment}
        equipmentSubtotal={calculations.equipmentSubtotal}
        preSelectItem={preSelectEquipment}
        onPreSelectHandled={clearPreSelect}
      />

      {/* Misc Section */}
      <MiscSection
        miscItems={bidSheet.misc_items || []}
        miscItemsFromSettings={miscItemsFromSettings}
        miscUnits={miscUnits}
        canManageJobs={canManageJobs}
        formatCurrency={formatCurrency}
        onAddItem={handleAddMisc}
        onEditItem={handleEditMisc}
        onDeleteItem={handleDeleteMisc}
        miscSubtotal={calculations.miscSubtotal}
        preSelectItem={preSelectMisc}
        onPreSelectHandled={clearPreSelect}
      />

      {/* Labor Section */}
      <LaborSection
        laborEntries={bidSheet.labor_entries}
        employeeRoles={employeeRoles}
        estimatedWeeks={bidSheet.estimated_weeks}
        canManageJobs={canManageJobs}
        formatCurrency={formatCurrency}
        onAddEntry={handleAddLabor}
        onEditEntry={handleEditLabor}
        onDeleteEntry={handleDeleteLabor}
        laborSubtotal={calculations.laborSubtotal}
      />

      {/* Summary Card */}
      <BidSummaryCard
        calculations={calculations}
        bidSheet={bidSheet}
        formatWholeDollars={formatWholeDollars}
        formatCurrency={formatCurrency}
        changeOrders={changeOrders}
        effectiveTotal={effectiveTotal}
      />
    </div>
  );
};

export default BidSheetEditor;
